import pageProperties from '../pageProperties';

/**
 *  Constant used in a few places to determine selection rectString
 *  always add the knobs 7 pdf pixels offset from the rectString
 */
const selectionOffset = 7;

/**
 *  Take a canvas coordinate and convert it to a pdf coordinate
 *  Accepts { x, y } and returns { x, y }
 */
const convertPointToPDFCoords = (canvasPoint, pageNumber) => {
  const {
    scale,
    dpiScale,
    pdfPageHeight,
    offset
  } = pageProperties.getCanvasProperties(pageNumber);
  const scaleDPI = scale / dpiScale;
  const xLocationInPDFCoords = canvasPoint.x / scaleDPI + offset.x;
  const yLocationInPDFCoords = pdfPageHeight - canvasPoint.y / scaleDPI;
  return {
    x: xLocationInPDFCoords,
    y: yLocationInPDFCoords
  };
};

/**
 *  Take a pdf coordinate and convert it to a canvas coordinate
 *  Accepts { x, y } and returns { x, y }
 */
const convertPointToCanvasCoords = (pdfPoint, pageNumber) => {
  const {
    scale,
    // dpiScale,
    pdfPageHeight,
    offset
  } = pageProperties.getCanvasProperties(pageNumber);
  return {
    x: (pdfPoint.x - offset.x) * scale,
    y: (pdfPageHeight - pdfPoint.y) * scale
  };
};

/**
 *  Parse a number string
 *  return a float
 */
const parseNumberString = val => {
  let safeValue = val;
  if (typeof safeValue === 'string') {
    safeValue = parseFloat(safeValue, 10);
  }
  if (!safeValue && safeValue !== 0) {
    return 0;
  }
  return safeValue;
};

/**
 *  Parse an rgba color string "0.5 0 0.77 1"
 *  return rgba an array of numbers [0.5, 0, 0.77, 1]
 */
const parseRGBAString = color => {
  if (!color || !/(\d*\.?\d*\s){3}\d*\.?\d*/.test(color)) {
    return parseRGBAString('0 0 0 1');
  }
  return color.split(' ').map(next => parseNumberString(next));
};

/**
 *  Parse an rgb or rgba color object { red: 0.5, green: 0, blue: 0.77, alpha: 1.0 }
 *  return an rgba array of numbers [0.5, 0, 0.77, 1]
 */
const parseRGBAObject = color => {
  if (Object.keys(color || {}).length === 0) {
    return parseRGBAObject({
      red: 0,
      green: 0,
      blue: 0,
      alpha: 1
    });
  }
  return [parseNumberString(color.red), parseNumberString(color.green), parseNumberString(color.blue), color.alpha !== undefined && color.alpha !== null ? color.alpha : 1];
};

/**
 *  Parse an rgb or rgba color string [0.5, 0, 0.77, 1]
 *  return an rgb object { red: 0.5, green: 0, blue: 0.77 }
 */
const toRGBAObject = colorString => {
  const [redString, greenString, blueString, alphaString] = parseRGBAString(colorString);
  const newColor = {
    red: parseNumberString(redString),
    green: parseNumberString(greenString),
    blue: parseNumberString(blueString)
  };
  if (alphaString) {
    newColor.alpha = parseNumberString(alphaString);
  }
  return newColor;
};

/**
 *  Parse an rgb or rgba color object { red: 0.5, green: 0, blue: 0.77 }
 *  return an rgba string "0.5 0 0.77 1"
 */
const toRGBAString = colorObject => {
  const [red, green, blue, alpha] = parseRGBAObject(colorObject);
  let newColor = `${parseNumberString(red)} ${parseNumberString(green)} ${parseNumberString(blue)} `;
  if (alpha !== undefined && alpha !== null) {
    newColor += alpha;
  } else {
    newColor += '1';
  }
  return newColor;
};

/**
 *  Parse an rgb or rgba color array [0.5 0 0.77 1]
 *  return an integer rgba array safe for CSS [127, 0, 196, 255]
 */
const createRGBASafeVersion = ([red, green, blue, alpha]) => [Math.floor(red * 255),
// must be integer 0-255
Math.floor(green * 255),
// must be integer 0-255
Math.floor(blue * 255),
// must be integer 0-255
alpha !== undefined && alpha !== null ? alpha : 1 // Show 100% alpha by default
];

/**
 *  Parse an rgb or rgba color object or string { red: 0.5, green: 0, blue: 0.77 } or "0.5 0 0.77 1"
 *  return an integer rgba array safe for CSS [127, 0, 196, 255]
 */
const parseColor = color => {
  if (color && typeof color === 'object') {
    return createRGBASafeVersion(parseRGBAObject(color));
  }
  return createRGBASafeVersion(parseRGBAString(color));
};

/**
 *  Parse an rgb or rgba color object or string { red: 0.5, green: 0, blue: 0.77 } or "0.5 0 0.77 1"
 *  return an rgb object or an rgba string suitible for saving (dependent on saveFormat arg)
 */
const toSaveColor = (color, saveFormat = 'string') => {
  let colorTypeString = true;
  if (color && typeof color === 'object') {
    colorTypeString = false;
  }
  // object passed in - convert to string...
  if (saveFormat === 'string') {
    return colorTypeString ? color : toRGBAString(color);
  }
  // default to object
  return colorTypeString ? toRGBAObject(color) : color;
};

/**
 *  Parse a rectString "{{x,y},{width,height}}"
 *  return an array of position and dimensions [x, y, width, height]
 */
const parseRectString = rectStr => rectStr.replace(/{|}/g, '').split(',').map(str => Number(str));

/**
 *  Converts the rectangle(s) string saved in the db to an array of objects, one for each rectangle
 *
 *  @method parseRectangles
 *
 *  @param {string} rectString - value from returned report document annotation
 *
 *  @returns {array} [{x, y, width, height},...] - x,y,w,h as numbers
 */
const parseRectangles = rectsString => {
  const parsed = rectsString.replace(/\//g, '&').split('&');
  return (parsed || []).map(rect => parseRectString(rect));
};

/**
 *  Converts the passed in rectangle array - with x,y,w,h, to a box 25% bigger for use in bounding
 *  box and selection annotation
 *
 *  @method convertToHalo
 *
 *  @param {Array} rectangle - [x, y, width, height]
 *
 *  @returns {Array} [x, y, width, height] - but now it's 25% bigger around the same center
 *
 *  (used in adaptPDFRects when navigation bounding box is calculated)
 */
const convertToHalo = ([originalX, originalY, originalWidth, originalHeight]) => {
  const scaleFactor = 1.25; // Halo box offset, always 25% bigger then original box

  const newWidth = originalWidth * scaleFactor;
  const newHight = originalHeight * scaleFactor;
  const adjustX = 0.5 * (scaleFactor - 1) * originalWidth;
  const adjustY = 0.5 * (scaleFactor - 1) * originalHeight;
  return [originalX - adjustX, originalY - adjustY, newWidth, newHight];
};

/**
 *  Accepts the annotation, page scale and pageHeight.  Parses the annotations rectString then
 *  create a `halo` selection transformed to use the upper left origin vs pdf lower left origin
 *
 *  @method adaptPDFRects
 *
 *  @param {Number} scale - combined page view scale factor
 *  @param {Number} pageHeight - height of page, used to swap origin location
 *  @param {Object} annotation - annotation data, used for rectString and annotation id
 *  @param {Object} originOffset - difference between media and crop box origin points {x: n, y: z}
 *
 *  @returns {Object} {x, y, width, height, annotation} <- mapped to current canvas/pdf page
 *
 *  (halo is used when navigation bounding box is calculated)
 */
const adaptPDFRects = (scale, pageHeight, annotation, originOffset, useHalo = false) => {
  const rectsToDraw = parseRectangles(annotation.rectString);
  return rectsToDraw.map(rectangle => {
    const [x, y, w, h] = useHalo ? convertToHalo(rectangle) : rectangle;
    const newX = x * scale;
    const newY = pageHeight - y * scale;
    const newWidth = w * scale;
    const newHeight = h * scale;
    // coordinate system discrepency, lower left (pdf) vs upper left (canvas)
    const yAdjusted = newY - newHeight;
    return {
      x: newX - originOffset.x * scale,
      y: yAdjusted + originOffset.y * scale,
      w: newWidth,
      h: newHeight,
      annotation
    };
  });
};

/**
 *  Parse a pathString into an array
 *  Returns an array of points scaled to the canvas space
 */
const parsePath = pathString => {
  const parsed = pathString.replace(/\//g, '&').split('&');
  return (parsed || []).map(point => {
    const pointObj = point.replace(/{|}/g, '').split(',');
    const [x, y] = (pointObj || []).map(str => Number(str));
    return {
      x,
      y
    };
  });
};

/**
 *  Parse a pathString into an array
 *  Returns an array of points scaled to the canvas space
 */
const adaptPathStringToCanvas = (pathString, pageHeight, scale, dpiScale, originOffset) => parsePath(pathString).map(({
  x,
  y
}) => {
  const newX = x * scale;
  const newY = pageHeight - y * scale;
  return [(newX - originOffset.x * scale) / dpiScale, (newY + originOffset.y * scale) / dpiScale];
});

/**
 *  Parse and translate the passed in pathString by the provided x and y offset
 *  Returns a translated pathString
 */
const translatePathString = (pathString, offsetX, offsetY) => {
  const parsed = pathString.replace(/\//g, '&').split('&');
  const points = (parsed || []).map(point => {
    const pointObj = point.replace(/{|}/g, '').split(',');
    const [x, y] = (pointObj || []).map(str => Number(str));
    const newX = x + offsetX;
    const newY = y - offsetY;
    // point looks like this: "{x, y}"
    return `{${newX}, ${newY}}`;
  });
  // string looks like this: "{x1, y1}/{x2, y2}/{x3, y3}/..."
  const translatedPathString = points.join('/');
  return translatedPathString;
};

// parses the old rectString, then applies the x and y offset and returns a new rect string
const translateRectString = (rectString, offsetX, offsetY) => {
  const [oldRectX, oldRectY, oldRectW, oldRectH] = parseRectangles(rectString)[0];
  const rectX = oldRectX + offsetX;
  const rectY = oldRectY - offsetY;
  const newRectString = `{{${rectX}, ${rectY}}, {${oldRectW}, ${oldRectH}}}`;
  return newRectString;
};

/**
 *  Parse the annotations pathString and rectString as well as the key that was dragged to scale the
 *  annotation rect and it's new position, then determine the scale and scale the path.
 *  Returns a pathString
 */
const scalePathString = (originalPathString, originalRectString, newKnobPDFCoords, key) => {
  // parse the original rectString to get it's corners (scale)
  const [originalX, originalY, originalW, originalH] = parseRectString(originalRectString);
  const originalCorners = {
    ll: {
      x: originalX,
      y: originalY
    },
    ul: {
      x: originalX,
      y: originalY + originalH
    },
    ur: {
      x: originalX + originalW,
      y: originalY + originalH
    },
    lr: {
      x: originalX + originalW,
      y: originalY
    }
  };

  /* We use a "dilation transform" here, this essentially means we choose one point to 'dilate' our
   * shape around. In this case, the dilation point is the opposite point from the altered selection
   * knob. For example, moving the upper right (ur) knob means that the lower left (ll) knob is the
   * dilation point for the transform. We scale all points in a pathString or rect corners around
   * the dilation point.
   */
  let dilationPoint;
  let newWidth = 1;
  let newHeight = 1;

  // self imposed min width and height - similar to those set on the iPad
  const minWidth = 30;
  const minHeight = 30;

  // extract the x and y position of the altered knob
  const {
    x,
    y
  } = newKnobPDFCoords;
  if (key === 'll') {
    // lower left is being moved
    dilationPoint = originalCorners.ur;
    const newCornerPosition = {
      x: x + selectionOffset,
      // adjust position up to the left of knob to true
      y: y + selectionOffset
    };
    newWidth = dilationPoint.x - newCornerPosition.x;
    newHeight = dilationPoint.y - newCornerPosition.y;
  } else if (key === 'ul') {
    dilationPoint = originalCorners.lr;
    const newCornerPosition = {
      x: x + selectionOffset,
      // adjust position down to the right of knob to true
      y: y - selectionOffset
    };
    newWidth = dilationPoint.x - newCornerPosition.x;
    newHeight = newCornerPosition.y - dilationPoint.y;
  } else if (key === 'ur') {
    dilationPoint = originalCorners.ll;
    const newCornerPosition = {
      x: x - selectionOffset,
      // adjust position down to the left of knob to true
      y: y - selectionOffset
    };
    newWidth = newCornerPosition.x - dilationPoint.x;
    newHeight = newCornerPosition.y - dilationPoint.y;
  } else if (key === 'lr') {
    dilationPoint = originalCorners.ul;
    const newCornerPosition = {
      x: x - selectionOffset,
      // adjust position up to the left of knob to true
      y: y + selectionOffset
    };
    newWidth = newCornerPosition.x - dilationPoint.x;
    newHeight = dilationPoint.y - newCornerPosition.y;
  }

  // apply minimums if need be
  const adjustedNewWidth = newWidth >= minWidth ? newWidth : minWidth;
  const adjustedNewHeight = newHeight >= minHeight ? newHeight : minHeight;

  // determine the scale factor, but if the original height or width is zero, use the adjusted w/h
  const scaleX = originalW ? adjustedNewWidth / originalW : adjustedNewWidth;
  const scaleY = originalH ? adjustedNewHeight / originalH : adjustedNewHeight;

  // use the pathString if it's an ink annotation - otherwise use the rectString corner points
  const pointsToTranslate = originalPathString.length // if it's got length it's an ink annotation
  ? parsePath(originalPathString) : Object.keys(originalCorners).map(c => originalCorners[c]);

  // translate the path array such that the dilation point is the origin (0,0)
  const translatedToZeroPoints = pointsToTranslate.map(next => ({
    x: next.x - dilationPoint.x,
    y: next.y - dilationPoint.y
  }));

  // scale all points in the array
  const scaledPoints = translatedToZeroPoints.map(next => ({
    x: next.x * Math.abs(scaleX),
    y: next.y * Math.abs(scaleY)
  }));

  // translate the path array such that the dilation point is back to where it originally was
  const newPointsArray = scaledPoints.map(next => ({
    x: next.x + dilationPoint.x,
    y: next.y + dilationPoint.y
  }));

  // generate the new pathString (and faux pathString for freeText transformed rectString corners)
  return newPointsArray.map(next => `{${next.x}, ${next.y}}`).join('/');
};

/**
 *  Create the rectString from a pathString
 *  Returns a rectString
 */
const recalcRectStringFromPath = pathString => {
  const parsed = pathString.replace(/\//g, '&').split('&');
  const xPoints = [];
  const yPoints = [];
  parsed.forEach(point => {
    let pointObj = point.replace(/{|}/g, '').split(',');
    pointObj = (pointObj || []).map(str => Number(str));
    xPoints.push(pointObj[0]);
    yPoints.push(pointObj[1]);
  });

  // string looks like this: "..."
  const xmin = Math.min(...xPoints);
  const xmax = Math.max(...xPoints);
  const ymin = Math.min(...yPoints);
  const ymax = Math.max(...yPoints);
  return `{{${xmin}, ${ymin}}, {${xmax - xmin}, ${ymax - ymin}}}`;
};

/**
 *  Replace the beginning or end of the line with the passed in end/beginning
 *  return a line (ink annotation with 2 points) with the new end or beginning
 */
const returminateLine = (originalPathString, newTerminationPoint, key) => {
  const parsed = originalPathString.replace(/\//g, '&').split('&');
  const points = parsed.map((point, index) => {
    const pointObj = point.replace(/{|}/g, '').split(',');
    const [x, y] = (pointObj || []).map(str => Number(str));
    if (index === 0 && key === 'beginning') {
      return `{${newTerminationPoint.x}, ${newTerminationPoint.y}}`;
    }
    if (index === 1 && key === 'end') {
      return `{${newTerminationPoint.x}, ${newTerminationPoint.y}}`;
    }
    return `{${x}, ${y}}`;
  });
  // string looks like this: "{x1, y1}/{x2, y2}/{x3, y3}/..."
  const translatedPathString = points.join('/');
  return translatedPathString;
};

/**
 *  Complimentary to scalePathString, calculates the four corners with knobs and moves target
 *  to the corner specified by clickedName
 *  does not return anything - just alters `target`
 */
const moveTargetToProperPosition = (target, clickedName, newRectString, pageNumber) => {
  if (['ul', 'ur', 'll', 'lr'].includes(clickedName)) {
    // reset the selected knob to the new selected corner or end
    const [newX, newY, newW, newH] = parseRectString(newRectString);
    const newKCorners = {
      ll: {
        x: newX - selectionOffset,
        y: newY - selectionOffset
      },
      ul: {
        x: newX - selectionOffset,
        y: newY + newH + selectionOffset
      },
      ur: {
        x: newX + newW + selectionOffset,
        y: newY + newH + selectionOffset
      },
      lr: {
        x: newX + newW + selectionOffset,
        y: newY - selectionOffset
      }
    };
    const newKnob = convertPointToCanvasCoords(newKCorners[clickedName], pageNumber);
    target.setAttr('x', newKnob.x);
    target.setAttr('y', newKnob.y);
  }
};

/**
 * take a DOMRect object and map it to our PDF specification
 */
const toRectString = box => `{{${box.x}, ${box.y}}, {${box.width}, ${box.height}}}`;
const toRectsString = boxes => boxes.map(toRectString).join('/');

/**
 * determine if any existing highlight annotations were selected from where the click event occurred
 */
const getHighlightsFromCursorPosition = (e, highlightsForPage, page) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const cursorPosition = {
    x: e.clientX - rect.left,
    // x position within the element.
    y: e.clientY - rect.top // y position within the element.
  };
  const cursorInPDFCoords = convertPointToPDFCoords(cursorPosition, page);
  return highlightsForPage.filter(annotation => {
    let isOverlapping = false;
    if (annotation.highlightAnnotation && annotation.highlightAnnotation.rectsString) {
      const parsedRectangles = parseRectangles(annotation.highlightAnnotation.rectsString);
      isOverlapping = parsedRectangles.some(rectangle => {
        const minX = rectangle[0]; // x
        const maxX = rectangle[0] + rectangle[2]; // x + width
        const minY = rectangle[1]; // y
        const maxY = rectangle[1] + rectangle[3]; // y + height

        // does the cursorInPDFCoords fall in between min/max range?
        return cursorInPDFCoords.x > minX && cursorInPDFCoords.x < maxX && cursorInPDFCoords.y > minY && cursorInPDFCoords.y < maxY;
      });
    }
    return isOverlapping;
  });
};

/**
 * from a user's selection of text, calculate the PDF rectsString values for db storage
 */
const getRectsStringFromSelection = (e, textLayer, textSelection, page) => {
  const {
    scale,
    dpiScale
  } = pageProperties.getCanvasProperties(page);
  const scaleDPI = scale / dpiScale;
  const textRange = textSelection.getRangeAt(0);
  const textRects = textRange.getClientRects();
  const pdfViewer = global.document.getElementById('pdf-viewer-body');
  const textLayerTop = textLayer.getBoundingClientRect().top;
  const textLayerLeft = textLayer.getBoundingClientRect().left;
  const boxes = Object.keys(textRects).filter(key => {
    const rect = textRects[key];
    const prevRect = textRects[key - 1];
    // should not add dupliate rects...
    // in multi-line selections, we see near-duplicate DOMRectList. skip them by ignoring rects
    // that fall within 5 pixels of the previous rects' x/y
    const minDiff = 5;
    return !(prevRect && prevRect.y + minDiff > rect.y && prevRect.x + minDiff > rect.x);
  }).map(key => {
    const rect = textRects[key];
    const truePosition = {
      // IE and Edge don't have x and y coords saved, but they are === to left and top
      x: (rect.x || rect.left) + pdfViewer.scrollLeft - textLayerLeft,
      y: (rect.y || rect.top) + rect.height - textLayerTop
    };
    const {
      x,
      y
    } = convertPointToPDFCoords(truePosition, page);
    const convertedRect = {
      x,
      y,
      width: rect.width / scaleDPI,
      height: rect.height / scaleDPI
    };
    return convertedRect;
  });
  return toRectsString(boxes);
};

/**
 * Generate a rectString from the rectsString
 */
const getRectStringFromRectsString = rectsString => {
  const parsedRectangles = parseRectangles(rectsString);
  if (parsedRectangles.length <= 1) {
    return rectsString;
  }
  const [firstRect] = parsedRectangles;
  let minX = firstRect[0];
  let maxX = firstRect[0] + firstRect[2];
  let minY = firstRect[1];
  let maxY = firstRect[1] + firstRect[3];
  parsedRectangles.slice(1).forEach(([x, y, width, height]) => {
    if (x < minX) {
      // find smallest x
      minX = x;
    }
    if (y < minY) {
      // find smallest y
      minY = y;
    }
    if (x + width > maxX) {
      // find biggest x
      maxX = x + width;
    }
    if (y + height > maxY) {
      // find biggest y
      maxY = y + height;
    }
  });
  const box = {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
  return toRectString(box);
};

/**
 *  Parse the array of saved points and only return arrays of points that are on this page
 */
const groupPointsByPage = points => {
  const pointGroups = [];
  let pointGroup = [];
  let currentPage = null;
  points.forEach(next => {
    // if the current page !== the next point's page, then start a new group
    if (next.pageNumber !== currentPage) {
      if (pointGroup.length) {
        // the point group
        pointGroups.push(pointGroup); // save the last pointGroup
      }
      currentPage = next.pageNumber; // jump to the next page
      pointGroup = [next]; // create the new point group
    } else {
      pointGroup.push(next);
    }
  });
  // wrap up the last point group
  pointGroups.push(pointGroup);
  return pointGroups;
};
export default {
  selectionOffset,
  convertPointToPDFCoords,
  convertPointToCanvasCoords,
  parseNumberString,
  parseRGBAString,
  parseRGBAObject,
  toRGBAObject,
  toRGBAString,
  parseColor,
  toSaveColor,
  parseRectString,
  parseRectangles,
  convertToHalo,
  adaptPDFRects,
  adaptPathStringToCanvas,
  translatePathString,
  translateRectString,
  scalePathString,
  recalcRectStringFromPath,
  returminateLine,
  toRectsString,
  moveTargetToProperPosition,
  getHighlightsFromCursorPosition,
  getRectsStringFromSelection,
  getRectStringFromRectsString,
  groupPointsByPage
};